import React from 'react';
import { Chip, Paper } from '@c2fo/liquidity';
import { formatUserStatus } from '../../../utils/userStatus';
import { formatDateWithTimezone } from '../../../utils/dateFormat';
import { formatEmptyField } from '../../../utils/emptyField';
import {
  getRoleDisplayValue,
  getLeadSourceDisplayValue,
  getIndustryDisplayValue,
  getFiscalYearEndDisplayValue,
} from '../../../utils/enumDisplayValues';
import { getCountryDisplay } from '../../../utils/countryDisplay';
import { DetailRow } from './DetailRow';
import { ActionButtons } from './ActionButtons';
import { RegistrationLinkPanel } from './RegistrationLinkPanel';
import type { User, LoginHistory } from '../types';
import { Locale, SUPPORTED_LOCALES } from '@admin-ui-root/src/app/constants/locales';

interface UserDetailsTabProps {
  user: User;
  fundingPartnerCount: number;
  fundingPartnerTermsCount: number;
  loginHistory: LoginHistory[];
  userId: string | undefined;
  canImpersonate: boolean;
  impersonating: boolean;
  editing: boolean;
  sendingPasswordReset: boolean;
  resettingPassword: boolean;
  forgettingUser: boolean;
  onImpersonate: () => void;
  onEditUser: (userData: Record<string, unknown>) => Promise<void>;
  onSendPasswordResetEmail: () => Promise<void>;
  onResetPassword: (password: string) => Promise<void>;
  onForgetUser: () => Promise<void>;
}

export const UserDetailsTab: React.FC<UserDetailsTabProps> = ({
  user,
  fundingPartnerCount,
  fundingPartnerTermsCount,
  loginHistory,
  userId,
  canImpersonate,
  impersonating,
  editing: _editing,
  sendingPasswordReset: _sendingPasswordReset,
  resettingPassword: _resettingPassword,
  forgettingUser,
  onImpersonate,
  onEditUser: _onEditUser,
  onSendPasswordResetEmail: _onSendPasswordResetEmail,
  onResetPassword: _onResetPassword,
  onForgetUser,
}) => {
  return (
    <>
      <Paper elevation={1} square className="p-6 mb-8">
        <div className="flex gap-8 items-start">
          <dl className="flex-1">
            <DetailRow label="Auth Service UUID" value={formatEmptyField(user.authServiceUuid)} />
            <DetailRow label="User ID" value={formatEmptyField(user.id)} />
            <DetailRow label="Email Address" value={formatEmptyField(user.emailAddress)} />
            <DetailRow label="First Name" value={formatEmptyField(user.firstName)} />
            <DetailRow label="Last Name" value={formatEmptyField(user.lastName)} />
            <DetailRow label="Role" value={formatEmptyField(getRoleDisplayValue(user.role))} />
            <DetailRow label="Title" value={formatEmptyField(user.title)} />
            <DetailRow label="Primary Phone" value={formatEmptyField(user.primaryPhoneNumber)} />
            <DetailRow label="Secondary Phone" value={formatEmptyField(user.secondaryPhoneNumber)} />
            <DetailRow label="AR Department Email" value={formatEmptyField(user.arDepartmentEmailAddress)} />
            <DetailRow
              label="Fiscal Year End"
              value={formatEmptyField(getFiscalYearEndDisplayValue(user.fiscalYearEnd))}
            />
            <DetailRow label="Industry" value={formatEmptyField(getIndustryDisplayValue(user.industry))} />
            <DetailRow label="Contact Preference" value={formatEmptyField(user.contactPreference)} />
            <DetailRow label="Lead Source" value={formatEmptyField(getLeadSourceDisplayValue(user.leadSource))} />
            <DetailRow
              label="Funding Partner Terms"
              value={`${fundingPartnerTermsCount ? String(fundingPartnerTermsCount) : '-'} / ${fundingPartnerCount ? String(fundingPartnerCount) : '-'}`}
            />
          </dl>

          <dl className="flex-1">
            <DetailRow
              label="Email Verified"
              value={
                <Chip
                  type={user.emailVerified ? 'primary' : 'error'}
                  variant="filled"
                  size="xs"
                  label={user.emailVerified ? 'Yes' : 'No'}
                />
              }
            />
            <DetailRow
              label="Supplier Agreement"
              value={
                user.termsCondsFlag ? (
                  <Chip
                    type={user.emailVerified ? 'primary' : 'warning'}
                    variant="filled"
                    size="xs"
                    label={user.emailVerified ? 'Accepted' : 'Viewed'}
                  />
                ) : (
                  <Chip type="error" variant="filled" size="xs" label="No" />
                )
              }
            />
            <DetailRow
              label="Status"
              value={
                <Chip
                  type="default"
                  variant="filled"
                  size="xs"
                  label={formatUserStatus(typeof user.status === 'number' ? user.status : 0)}
                />
              }
            />
            <DetailRow label="Registration Date" value={formatDateWithTimezone(user.created)} />
            <DetailRow label="Created" value={formatDateWithTimezone(user.created)} />
            <DetailRow label="Created By" value={formatEmptyField(user.createdSource)} />
            <DetailRow label="Postal Code" value={formatEmptyField(user.postalCode)} />
            <DetailRow label="Country" value={getCountryDisplay(user.country)} />
            <DetailRow
              label="Language"
              value={formatEmptyField(
                SUPPORTED_LOCALES.find((locale: Locale) => locale.code === user.language)?.language,
              )}
            />
            <DetailRow label="Source" value={formatEmptyField(user.source)} />
            <DetailRow
              label="Last Login"
              value={loginHistory.length > 0 ? formatDateWithTimezone(loginHistory[0].created) : '-'}
            />
            <DetailRow label="Supplier Read-Only User" value={String(user.supplierReadOnly)} />
            <DetailRow label="Forgotten On" value={formatEmptyField(user.forgottenOn)} />
          </dl>
        </div>

        <ActionButtons
          user={user}
          canImpersonate={canImpersonate}
          impersonating={impersonating}
          editing={_editing}
          sendingPasswordReset={_sendingPasswordReset}
          resettingPassword={_resettingPassword}
          forgettingUser={forgettingUser}
          onImpersonate={onImpersonate}
          onEditUser={_onEditUser}
          onSendPasswordResetEmail={_onSendPasswordResetEmail}
          onResetPassword={_onResetPassword}
          onForgetUser={onForgetUser}
        />
      </Paper>

      {user && user.status === 1 && userId && <RegistrationLinkPanel userId={userId} userStatus={user.status} />}
    </>
  );
};
